import { ROUTES } from "./routes";
import { siteAsset } from "./siteAssets";

export type SiteTeamMember = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  note: string;
  img: string;
  focusX?: number;
  focusY?: number;
  zoom?: number;
};

type SiteTeamContent = {
  version?: number;
  updatedAt?: string;
  members: SiteTeamMember[];
};

const TEAM_URL = siteAsset("/content/team.json");
const GUESTS_URL = siteAsset("/content/guests.json");

export const fallbackTeamMembers: SiteTeamMember[] = [
  { id: "nastya", firstName: "Анастасия", lastName: "Рябых", img: "/actors/nastya.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "vica", firstName: "Виктория", lastName: "Юркова", img: "/actors/vica-2.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "lesha", firstName: "Алексей", lastName: "Филатов", img: "/actors/lesha.jpg", role: "актер", note: "Актёр театра «Дофамин»." },
  { id: "anton", firstName: "Антон", lastName: "Васильев", img: "/actors/anton.jpg", role: "актер", note: "Актёр театра «Дофамин»." },
  { id: "ksysha", firstName: "Ксения", lastName: "", img: "/actors/ksysha-2.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "grisha", firstName: "Григорий", lastName: "Найдёнов", img: "/actors/grisha.jpg", role: "актер", note: "Актёр театра «Дофамин»." },
  { id: "alena", firstName: "Алена", lastName: "Паршина", img: "/actors/alena.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "katya", firstName: "Екатерина", lastName: "Слыховская", img: "/actors/katya.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "polina", firstName: "Полина", lastName: "Смолкина", img: "/actors/polina.jpg", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "nika", firstName: "Вероника", lastName: "Атушева", img: "/actors/nika.JPG", role: "актриса", note: "Актриса театра «Дофамин»." },
  { id: "sergey", firstName: "Сергей", lastName: "Луняка", img: "/actors/ya.JPG", role: "худ. рук", note: "Художественный руководитель театра «Дофамин»." },
  { id: "lera", firstName: "Лера", lastName: "Буракова", img: "/actors/lera.jpg", role: "актриса", note: "Актриса театра «Дофамин»." },
];

const BUNDLED_ACTOR_FILES = new Map<string, string>([
  ["nastya.jpg", "nastya.JPG"],
  ["vica-2.jpg", "vica-2.JPG"],
  ["lesha.jpg", "lesha.jpg"],
  ["anton.jpg", "anton.jpg"],
  ["ksysha-2.jpg", "ksysha-2.JPG"],
  ["grisha.jpg", "grisha.jpg"],
  ["alena.jpg", "alena.JPG"],
  ["katya.jpg", "katya.JPG"],
  ["polina.jpg", "polina.jpg"],
  ["nika.jpg", "nika.JPG"],
  ["ya.jpg", "ya.JPG"],
  ["lera.jpg", "lera.jpg"],
]);

export function teamPhotoPosition(member: { focusX?: number; focusY?: number }) {
  const focusX = member.focusX ?? 50;
  const focusY = member.focusY ?? 16;
  return `${focusX}% ${focusY}%`;
}

export function teamPhotoZoom(member: { zoom?: number }) {
  const parsed = typeof member.zoom === "number" ? member.zoom : Number(member.zoom);
  if (!Number.isFinite(parsed)) return 1;
  const stepped = Math.round(parsed * 10) / 10;
  return Math.min(4, Math.max(1, stepped));
}

/** Локальные кадры команды. Оригиналы в хранилище бывают по 4–7 МБ и шире 4096px — телефон их часто не рисует. */
export function teamPhotoSrc(img: string): string {
  const trimmed = img.trim();
  const path = trimmed.split("?")[0]?.split("#")[0] ?? "";
  const file = path.split("/").filter(Boolean).pop() ?? "";
  const bundled = BUNDLED_ACTOR_FILES.get(file.toLowerCase());
  const fromActors = /\/actors\/[^/]+$/i.test(path);
  if (bundled && fromActors) return `/theater/actors/${bundled}`;
  return siteAsset(trimmed);
}

export function teamMemberName(member: SiteTeamMember): string {
  return [member.firstName, member.lastName].map((part) => part.trim()).filter(Boolean).join(" ");
}

export function teamMemberHref(id: string) {
  return `${ROUTES.ABOUTUS}#${encodeURIComponent(id)}`;
}

export function teamMemberIdFromHash(hash: string) {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return "";
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

export function findTeamMemberByActorName(actorName: string, members: SiteTeamMember[]) {
  const actor = normalizePersonName(actorName);
  if (!actor) return null;
  const exact = members.find((member) => normalizePersonName(teamMemberName(member)) === actor);
  if (exact) return exact;

  const [actorFirst, ...actorRest] = actor.split(" ");
  const actorLast = actorRest.join(" ");
  const scored = members.map((member) => ({
    member,
    firstScore: scoreNamePart(actorFirst, normalizePersonName(member.firstName)),
    lastScore: scoreNamePart(actorLast, normalizePersonName(member.lastName)),
  }));
  const byName = scored
    .filter((row) => row.firstScore >= 2 && row.lastScore >= 2)
    .sort((left, right) => right.firstScore + right.lastScore - (left.firstScore + left.lastScore));
  if (byName.length === 1) return byName[0].member;
  if (byName.length > 1 && byName[0].firstScore + byName[0].lastScore > byName[1].firstScore + byName[1].lastScore) {
    return byName[0].member;
  }

  const byFirstName = scored.filter(
    (row) => row.firstScore >= 3 && !normalizePersonName(row.member.lastName),
  );
  if (byFirstName.length === 1) return byFirstName[0].member;
  return null;
}

function normalizePersonName(value: string) {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^a-zа-я0-9\s-]/g, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function scoreNamePart(left: string, right: string) {
  if (!left || !right) return 0;
  if (left === right) return 4;
  if (left.length >= 3 && right.length >= 3 && (left.includes(right) || right.includes(left))) return 3;
  const distance = editDistance(left, right);
  const limit = Math.max(left.length, right.length) <= 6 ? 1 : 2;
  return distance <= limit ? 2 : 0;
}

function editDistance(left: string, right: string) {
  if (left === right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    const current = [row];
    for (let col = 1; col <= right.length; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      current[col] = Math.min(current[col - 1] + 1, previous[col] + 1, previous[col - 1] + cost);
    }
    previous = current;
  }
  return previous[right.length];
}

function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function normalizeMember(raw: unknown): SiteTeamMember | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const firstName = typeof row.firstName === "string" ? row.firstName.trim() : "";
  const lastName = typeof row.lastName === "string" ? row.lastName.trim() : "";
  const role = typeof row.role === "string" ? row.role.trim() : "";
  const note = typeof row.note === "string" ? row.note.trim() : "";
  const img = typeof row.img === "string" ? row.img.trim() : "";
  if (!id || (!firstName && !lastName)) return null;
  const focusX = readTeamFocus(row.focusX, 50);
  const focusY = readTeamFocus(row.focusY, 16);
  const zoom = readTeamZoom(row.zoom);
  return {
    id,
    firstName,
    lastName,
    role,
    note,
    img,
    ...(focusX === 50 ? null : { focusX }),
    ...(focusY === 16 ? null : { focusY }),
    ...(zoom <= 1 ? null : { zoom }),
  };
}

function readTeamFocus(value: unknown, fallback: number) {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

function readTeamZoom(value: unknown) {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return 1;
  const stepped = Math.round(parsed * 10) / 10;
  return Math.min(4, Math.max(1, stepped));
}

/** null — файла нет или он битый, страница берёт запасной состав. */
export async function fetchSiteTeam(): Promise<SiteTeamMember[] | null> {
  return fetchMemberList(TEAM_URL);
}

export async function fetchSiteGuests(): Promise<SiteTeamMember[]> {
  return (await fetchMemberList(GUESTS_URL)) ?? [];
}

async function fetchMemberList(url: string): Promise<SiteTeamMember[] | null> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    const parsed = safeJsonParse<SiteTeamContent>(await res.text());
    if (!parsed || !Array.isArray(parsed.members)) return null;
    return parsed.members.map(normalizeMember).filter((member): member is SiteTeamMember => member !== null);
  } catch {
    return null;
  }
}
